import React, { useContext, useEffect, useState } from 'react'
import { Button, Checkbox, Select, Switch } from 'antd'
import openNotification from 'utils/Notification'
import { GET_REVISED_RISK_BUTTON_SETTING } from '../graphql/Queries'
import client from 'apollo'
import { UPDATE_REVISED_RISK_BUTTON_SETTING } from '../graphql/Mutations'
import Loader from 'components/loaders/Loader'
import CustomMeta from 'components/CustomMeta'
import { getName, canUserEdit, canUserSave } from 'utils/User'
import Page404 from 'components/Page404'
import { GET_ALL_CLIENT_BY_TYPE } from 'modules/BulkClientReport/graphql/Queries'
import { AppContext } from 'AppContext'

const { Option } = Select

export default function RevisedRiskProfileButtonSetting() {
  const { state: { currentUser, userPermissions, isClient } = {} } =
    useContext(AppContext) || {}
  const canSubmit =
    canUserEdit(userPermissions || currentUser, isClient) &&
    canUserSave(userPermissions || currentUser, isClient)

  const [showButton, setShowButton] = useState(false)
  const [selectedClients, setSelectedClients] = useState([])
  const [isChecked, setIsChecked] = useState(false)
  const [loading, setLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [userLoading, setUserLoading] = useState(false)
  const [clientData, setClientData] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    setUserLoading(true)
    client
      .query({
        query: GET_ALL_CLIENT_BY_TYPE,
        variables: {
          where: {
            userType: 'active',
          },
          skip: 0,
          limit: 1000000,
        },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        setUserLoading(false)
        setClientData(res?.data?.getUserAccordingUserType || [])
      })
      .catch((err) => {
        console.error(err)
        setUserLoading(false)
        setError(err)
      })
  }, [])

  useEffect(() => {
    setLoading(true)
    client
      .query({
        query: GET_REVISED_RISK_BUTTON_SETTING,
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        setLoading(false)
        setShowButton(
          res?.data?.getRevisedRiskButtonSetting?.revisedRiskButtonSetting
        )
        setSelectedClients(
          JSON.parse(
            res?.data?.getRevisedRiskButtonSetting?.clientCodes || '[]'
          )
        )
        setIsChecked(
          res?.data?.getRevisedRiskButtonSetting?.toCheckConditions
        )
      })
      .catch((err) => {
        setLoading(false)
        console.error(err)
        setError(err)
      })
  }, [])

  const handlePaste = (event) => {
    const clipboardData = event.clipboardData || window.clipboardData
    const pastedText = clipboardData.getData('Text')?.trim()

    if (!pastedText) return

    const pastedCodes = pastedText.split(',').map((c) => c.trim().toLowerCase())

    const matchedClients = clientData
      .filter((user) =>
        pastedCodes.includes(user.clientCode.toLowerCase())
      )
      .map((user) => user.clientCode)

    if (matchedClients.length > 0) {
      setSelectedClients((prev) => [...new Set([...prev, ...matchedClients])])
    }
    event.preventDefault()
  }

  const updateData = () => {
    if (!canSubmit) return
    setUpdateLoading(true)
    client
      .mutate({
        mutation: UPDATE_REVISED_RISK_BUTTON_SETTING,
        variables: {
          data: {
            revisedRiskButtonSetting: showButton,
            clientCodes: JSON.stringify(selectedClients),
            toCheckConditions: isChecked,
          },
        },
      })
      .then(() => {
        setUpdateLoading(false)
        openNotification('success', 'Setting updated successfully')
      })
      .catch((err) => {
        console.log(err)
        setUpdateLoading(false)
        openNotification('error', 'An error occurred! Please try again')
      })
  }

  if (loading) return <Loader />
  if (error) return <Page404 error={error} />

  return (
    <>
      <CustomMeta title='Revised Risk Button Setting' />
      <div className='main_dashboard_div'>
        <div className='slot_booking_header'>
          <div className='slot_booking_header_title'>
            Revised Risk Button Setting
          </div>
        </div>

        <Switch
          disabled={!canSubmit}
          checked={showButton}
          onChange={(e) => setShowButton(e)}
        />

        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
          <Select
            disabled={!canSubmit}
            style={{ width: '50%' }}
            getPopupContainer={(trigger) => trigger.parentElement}
            placeholder='Please select client(s)'
            showSearch
            allowClear
            optionFilterProp='children'
            mode='multiple'
            loading={userLoading}
            value={selectedClients}
            onChange={(val) => setSelectedClients(val)}
            onPaste={handlePaste}
          >
            {clientData.map((user, key) => {
              const value = user.clientCode
              return (
                <Option key={key} value={value} title={getName(user)}>
                  {`${value} - ${getName(user)}`}
                </Option>
              )
            })}
          </Select>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
          <Checkbox
            disabled={!canSubmit}
            defaultChecked={isChecked}
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
          >
            Check Conditions
          </Checkbox>
        </div>

        <div style={{ marginTop: 20 }}>
          <Button
            type='primary'
            htmlType='submit'
            loading={updateLoading}
            onClick={updateData}
            disabled={!canSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  )
}
